import {
  Component,
  OnDestroy,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { Capacitor } from '@capacitor/core';
import {
  AlertController,
  ModalController,
  ToastController,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Geolocation } from '@capacitor/geolocation';
import { CameraPreview, CameraPreviewFlashMode } from '@capgo/camera-preview';
import {
  AndroidSettings,
  IOSSettings,
  NativeSettings,
} from 'capacitor-native-settings';
import { ModalRole } from '../models/modal-roles';

interface CapturedPhoto {
  path: string;
  src: string;
}

const BODY_CLASS = 'multi-camera-active';
const FLASH_MODES: CameraPreviewFlashMode[] = ['off', 'on', 'auto'];
const LOCATION_SERVICES_DISABLED = 'OS-PLUG-GLOC-0007';

@Component({
  selector: 'lib-multi-camera',
  templateUrl: './multi-camera.component.html',
  styleUrls: ['./multi-camera.component.scss'],
  // Styles must reach body/ion-app so the native preview behind the WebView is visible.
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class MultiCameraComponent implements OnInit, OnDestroy {
  photos = signal<CapturedPhoto[]>([]);
  capturing = signal(false);
  finishing = signal(false);
  flashMode = signal<CameraPreviewFlashMode>('off');
  shutterBlackout = signal(false);
  zoomMin = signal(1);
  zoomMax = signal(1);
  zoomLevel = signal(1);
  private started = false;
  private pinchStartDistance = 0;
  private pinchStartZoom = 1;
  private zoomRequestInFlight = false;
  private pendingZoomLevel: number | null = null;

  constructor(
    private alertController: AlertController,
    private modalController: ModalController,
    private toastController: ToastController,
    private translate: TranslateService
  ) {}

  async ngOnInit() {
    document.body.classList.add(BODY_CLASS);
    try {
      if (Capacitor.isNativePlatform()) {
        let locationPermission;
        try {
          locationPermission = await Geolocation.requestPermissions({
            permissions: ['coarseLocation'],
          });
        } catch (e) {
          if ((e as { code?: string }).code !== LOCATION_SERVICES_DISABLED) {
            throw e;
          }
          await this.cancel();
          await this.showLocationServicesDisabledAlert();
          return;
        }
        if (locationPermission.coarseLocation !== 'granted') {
          await this.cancel();
          await this.showLocationPermissionAlert();
          return;
        }
      }

      const permission = await CameraPreview.requestPermissions({
        disableAudio: true,
        showSettingsAlert: true,
        title: 'Kein Kamerazugriff',
        message:
          'Leider hat die App keinen Kamerazugriff. Bitte ändere die Berechtigungen in den Einstellungen.',
        openSettingsButtonTitle: 'Einstellungen öffnen',
      });
      if (permission.camera !== 'granted') {
        await this.showError('camera.permissionDenied');
        await this.cancel();
        return;
      }
      await CameraPreview.start({
        position: 'rear',
        toBack: true,
        storeToFile: true,
        disableAudio: true,
        aspectMode: 'cover',
        rotateWhenOrientationChanged: false,
        lockAndroidOrientation: true,
        x: 0,
        y: 0,
        width: window.innerWidth,
        height: window.innerHeight,
      });
      this.started = true;
      await this.refreshZoom();
    } catch (e) {
      console.error(e);
      await this.showError('camera.startError');
      await this.cancel();
    }
  }

  async ngOnDestroy() {
    document.body.classList.remove(BODY_CLASS);
    await this.stopPreview();
  }

  async capture() {
    if (!this.started || this.capturing()) {
      return;
    }
    this.capturing.set(true);
    this.shutterBlackout.set(true);
    setTimeout(() => this.shutterBlackout.set(false), 100);
    try {
      const { value } = await CameraPreview.capture({
        quality: 100,
        withExifLocation: true,
      });
      this.photos.update((p) => [
        ...p,
        { path: value, src: Capacitor.convertFileSrc(value) },
      ]);
    } catch (e) {
      console.error(e);
      await this.showError('camera.captureError');
    } finally {
      this.capturing.set(false);
    }
  }

  async remove(photo: CapturedPhoto) {
    this.photos.update((p) => p.filter((x) => x !== photo));
    await this.deleteFiles([photo]);
  }

  async toggleFlash() {
    const next =
      FLASH_MODES[
        (FLASH_MODES.indexOf(this.flashMode()) + 1) % FLASH_MODES.length
      ];
    try {
      await CameraPreview.setFlashMode({ flashMode: next });
      this.flashMode.set(next);
    } catch (e) {
      console.error(e);
    }
  }

  async flip() {
    try {
      await CameraPreview.flip();
      await this.refreshZoom();
    } catch (e) {
      console.error(e);
    }
  }

  async changeZoom(
    event: CustomEvent<{
      value: number | { lower: number; upper: number };
    }>
  ) {
    const value = event.detail.value;
    await this.setZoomLevel(typeof value === 'number' ? value : value.lower);
  }

  async adjustZoom(amount: number) {
    this.setZoomLevel(this.zoomLevel() + amount);
  }

  onTouchStart(event: TouchEvent) {
    if (event.touches.length !== 2) {
      return;
    }
    this.pinchStartDistance = this.touchDistance(event.touches);
    this.pinchStartZoom = this.zoomLevel();
  }

  onTouchMove(event: TouchEvent) {
    if (event.touches.length !== 2 || this.pinchStartDistance === 0) {
      return;
    }
    event.preventDefault();
    const scale = this.touchDistance(event.touches) / this.pinchStartDistance;
    this.setZoomLevel(this.pinchStartZoom * scale);
  }

  onTouchEnd(event: TouchEvent) {
    if (event.touches.length < 2) {
      this.pinchStartDistance = 0;
    }
  }

  private touchDistance(touches: TouchList): number {
    const deltaX = touches[0].clientX - touches[1].clientX;
    const deltaY = touches[0].clientY - touches[1].clientY;
    return Math.hypot(deltaX, deltaY);
  }

  private async refreshZoom() {
    const zoom = await CameraPreview.getZoom();
    this.zoomMin.set(zoom.min);
    this.zoomMax.set(zoom.max);
    this.zoomLevel.set(zoom.current);
  }

  private setZoomLevel(level: number) {
    const boundedLevel = Math.max(
      this.zoomMin(),
      Math.min(this.zoomMax(), level)
    );
    this.zoomLevel.set(boundedLevel);
    this.pendingZoomLevel = boundedLevel;
    if (!this.zoomRequestInFlight) {
      void this.flushZoomRequests();
    }
  }

  private async flushZoomRequests() {
    this.zoomRequestInFlight = true;
    while (this.pendingZoomLevel !== null) {
      const level = this.pendingZoomLevel;
      this.pendingZoomLevel = null;
      try {
        await CameraPreview.setZoom({ level });
      } catch (e) {
        console.error(e);
        await this.refreshZoom();
      }
    }
    this.zoomRequestInFlight = false;
  }

  async done() {
    if (this.finishing()) {
      return;
    }
    this.finishing.set(true);
    const photos = this.photos();
    try {
      const blobs = await Promise.all(
        photos.map((p) => fetch(p.src).then((r) => r.blob()))
      );
      await this.stopPreview();
      await this.deleteFiles(photos);
      await this.modalController.dismiss(blobs, ModalRole.Confirm);
    } catch (e) {
      console.error(e);
      this.finishing.set(false);
      await this.showError('camera.captureError');
    }
  }

  async cancel() {
    await this.stopPreview();
    await this.deleteFiles(this.photos());
    await this.modalController.dismiss(null, ModalRole.Cancel);
  }

  private async stopPreview() {
    if (!this.started) {
      return;
    }
    this.started = false;
    try {
      await CameraPreview.stop();
    } catch (e) {
      console.error(e);
    }
  }

  private async deleteFiles(photos: CapturedPhoto[]) {
    await Promise.all(
      photos.map((p) =>
        CameraPreview.deleteFile({ path: p.path }).catch((): void => undefined)
      )
    );
  }

  private async showError(key: string) {
    const toast = await this.toastController.create({
      message: this.translate.instant(key),
      duration: 3000,
      color: 'danger',
    });
    await toast.present();
  }

  private async showLocationPermissionAlert() {
    const alert = await this.alertController.create({
      header: this.translate.instant('gps.permissionDenied'),
      message: this.translate.instant('gps.settingsMessage'),
      buttons: [
        {
          text: this.translate.instant('common.cancel'),
          role: 'cancel',
        },
        {
          text: this.translate.instant('gps.openSettings'),
          handler: () => {
            void NativeSettings.open({
              optionAndroid: AndroidSettings.ApplicationDetails,
              optionIOS: IOSSettings.App,
            }).catch((e) => console.error(e));
          },
        },
      ],
    });
    await alert.present();
  }

  private async showLocationServicesDisabledAlert() {
    const isAndroid = Capacitor.getPlatform() === 'android';
    const alert = await this.alertController.create({
      header: this.translate.instant('gps.disabled'),
      message: this.translate.instant('gps.disabledMessage'),
      buttons: isAndroid
        ? [
            {
              text: this.translate.instant('common.cancel'),
              role: 'cancel',
            },
            {
              text: this.translate.instant('gps.openSettings'),
              handler: () => {
                void NativeSettings.openAndroid({
                  option: AndroidSettings.Location,
                }).catch((e) => console.error(e));
              },
            },
          ]
        : ['OK'],
    });
    await alert.present();
  }
}
